import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FileEdit, Clock, Calendar, FileText } from 'lucide-react';

import InboxHeader from '../../components/Inbox/InboxHeader';
import ActionButtons from '../../components/Inbox/ActionButtons';
import RemarksHistory from '../../components/Inbox/RemarksHistory';
import InboxSplitLayout from '../../components/Inbox/InboxSplitLayout';
import { SimpleCommentInput } from '../../components/Inbox/VerificationInput';

import {
    fetchIndentAmendQueue,
    fetchIndentAmendDetail,
    submitIndentAmendVerification,
    clearIndentAmendDetail,
    clearVerifyResult,
    resetIndentAmendVerification,
    selectIAQueue, selectIAQueueLoading, selectIAQueueError,
    selectIADetail, selectIADetailLoading,
    selectIAVerifyLoading,
} from '../../slices/purchaseSlice/indentAmendVerificationSlice';

import {
    fetchStatusList,
    selectEnabledActions,
    selectHasActions,
    selectStatusListLoading,
    selectStatusListError,
    resetApprovalData,
    setShowReturnButton,
} from '../../slices/CommonSlice/getStatusSlice';

import {
    fetchRemarks,
    selectRemarks,
    selectRemarksLoading,
    setSelectedMOID,
} from '../../slices/supplierPOSlice/purcahseHelperSlice';

const num = (v) => parseFloat(v || 0) || 0;
const money = (v) => num(v).toLocaleString('en-IN', { minimumFractionDigits: 2 });

// Legacy's own dropdown-building JS always drops the "Return" status for this page.
const EXCLUDED_ACTIONS = ['return'];

const ConfirmModal = ({ title, message, confirmLabel = 'OK', cancelLabel = 'Cancel', onConfirm, onCancel }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-5 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white">{title}</h3>
            </div>
            <div className="p-5 text-sm text-gray-700 dark:text-gray-300">{message}</div>
            <div className="p-4 flex justify-end gap-2 border-t border-gray-200 dark:border-gray-700">
                <button onClick={onCancel} className="px-4 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-semibold">{cancelLabel}</button>
                <button onClick={onConfirm} className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold">{confirmLabel}</button>
            </div>
        </div>
    </div>
);

const VerifyIndentAmend = ({ notificationData, onNavigate }) => {
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID;

    const queue = useSelector(selectIAQueue);
    const queueLoading = useSelector(selectIAQueueLoading);
    const queueError = useSelector(selectIAQueueError);
    const detail = useSelector(selectIADetail);
    const detailLoading = useSelector(selectIADetailLoading);
    const verifyLoading = useSelector(selectIAVerifyLoading);

    const statusLoading = useSelector(selectStatusListLoading);
    const statusError = useSelector(selectStatusListError);
    const enabledActions = useSelector(selectEnabledActions);
    const hasActions = useSelector(selectHasActions);

    const remarks = useSelector(selectRemarks);
    const remarksLoading = useSelector(selectRemarksLoading);

    const { InboxTitle, ModuleDisplayName } = notificationData || {};

    const [selectedItem, setSelectedItem] = useState(null);
    const [comment, setComment] = useState('');
    const [showRemarksHistory, setShowRemarksHistory] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isLeftPanelHovered, setIsLeftPanelHovered] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);

    const getCurrentUser = () => userData?.userName || userDetails?.userName || 'system';

    useEffect(() => {
        if (roleId) dispatch(fetchIndentAmendQueue({ roleId, username: getCurrentUser() }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roleId]);

    useEffect(() => {
        dispatch(setShowReturnButton('No'));
        return () => {
            dispatch(resetIndentAmendVerification());
            dispatch(resetApprovalData());
        };
    }, [dispatch]);

    useEffect(() => {
        if (selectedItem) {
            dispatch(fetchIndentAmendDetail({ cccode: selectedItem.CCCode, amendId: selectedItem.AmendId, indentNo: selectedItem.IndentNo }));
            setComment('');
            setShowRemarksHistory(false);
            setIsLeftPanelCollapsed(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem]);

    // Once the amendment header (with its MOID) arrives, load the status dropdown + history.
    useEffect(() => {
        if (!detail) return;
        dispatch(fetchStatusList({ MOID: detail.MOID, ROID: roleId, ChkAmt: detail.DifferenceValue || 0 }));
        dispatch(setSelectedMOID(detail.MOID));
        dispatch(fetchRemarks({ trno: detail.AmendId, moid: detail.MOID }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detail]);

    const handleBackToInbox = () => {
        if (onNavigate) onNavigate('dashboard', { name: 'Dashboard', type: 'dashboard' });
    };

    const handleRefresh = () => {
        if (roleId) dispatch(fetchIndentAmendQueue({ roleId, username: getCurrentUser() }));
        if (selectedItem) dispatch(fetchIndentAmendDetail({ cccode: selectedItem.CCCode, amendId: selectedItem.AmendId, indentNo: selectedItem.IndentNo }));
    };

    // Rows with Status === '0' are awaiting an update from the indent raiser — legacy skips
    // loading a detail view for these (they show a "Return" badge only).
    const handleItemSelect = (item) => {
        if (item.Status === '0') { toast.info('This amendment is pending update from the raiser — nothing to verify yet.'); return; }
        setSelectedItem(item);
    };

    const finishAndRefresh = () => {
        setTimeout(() => {
            dispatch(fetchIndentAmendQueue({ roleId, username: getCurrentUser() }));
            setSelectedItem(null);
            setComment('');
            setShowRemarksHistory(false);
            setIsLeftPanelCollapsed(false);
            dispatch(clearIndentAmendDetail());
            dispatch(clearVerifyResult());
            dispatch(resetApprovalData());
        }, 800);
    };

    const submitAction = async (actionValue) => {
        const payload = {
            IndentNo: selectedItem.IndentNo,
            ApprovalNote: comment.trim(),
            Action: actionValue,
            CCCode: selectedItem.CCCode,
            AmendId: selectedItem.AmendId,
            RoleId: roleId,
            Createdby: getCurrentUser(),
        };
        try {
            const result = await dispatch(submitIndentAmendVerification(payload)).unwrap();
            const status = typeof result === 'string' ? result : result?.Data;
            if (status && status !== 'Submited') { toast.error(status); return; }
            const verb = { Verify: 'Verified', Approve: 'Approved', Reject: 'Rejected', Return: 'Returned' }[actionValue] || `${actionValue}d`;
            toast.success(`Indent Amend ${verb} successfully`);
            finishAndRefresh();
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Failed to submit verification');
        }
    };

    const handleActionClick = (action) => {
        if (!selectedItem || !detail) { toast.error('No amendment selected'); return; }
        if (!comment.trim()) { toast.error('Enter Note'); return; }

        const actionValue = action.value || action.type;

        // Legacy always confirms before closing the indent when this amendment is flagged
        // CloseStatus='Yes' — declining aborts the submit entirely (it does not fall back to a
        // non-closing save), telling the user to use Reject instead.
        if (detail.CloseStatus === 'Yes') {
            setPendingAction(actionValue);
            return;
        }
        submitAction(actionValue);
    };

    const filteredQueue = queue.filter((item) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return item.IndentNo?.toLowerCase().includes(q) || item.CCName?.toLowerCase().includes(q) || item.CCCode?.toLowerCase().includes(q);
    });

    const renderItemCard = (item) => (
        <div className="p-4">
            <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 rounded-full border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
                    <FileEdit className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">{item.IndentNo}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.CCName}</p>
                </div>
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                <div className="flex items-center justify-between">
                    <span className="flex items-center space-x-1"><Calendar className="w-3 h-3" /><span>{item.AmendDate}</span></span>
                    {item.Status === '0' ? (
                        <span className="px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-[10px]">Return</span>
                    ) : (
                        <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[10px]">₹{money(item.DifferenceAmount)}</span>
                    )}
                </div>
            </div>
        </div>
    );

    const renderListItem = (item) => (
        <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm">
            <span className="font-semibold text-gray-900 dark:text-white min-w-[120px] truncate">{item.IndentNo}</span>
            <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-xs">{item.CCName}</span>
            <span className="flex items-center gap-1 text-gray-500 dark:text-gray-400 min-w-[100px]"><Calendar className="w-3 h-3" /> {item.AmendDate}</span>
            {item.Status === '0' ? (
                <span className="ml-auto px-2 py-0.5 text-xs rounded-full border bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 whitespace-nowrap">Return</span>
            ) : (
                <span className="ml-auto text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">₹{money(item.DifferenceAmount)}</span>
            )}
        </div>
    );

    const renderCollapsedItem = () => (
        <div className="w-full h-full rounded-lg border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
            <FileEdit className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </div>
    );

    const renderDetailContent = () => {
        if (!selectedItem) return null;
        const items = detail?.AmendItemsList || [];

        return (
            <div className="space-y-6">
                {detailLoading && (
                    <div className="flex items-center justify-center gap-3 py-8"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div><span className="text-sm text-gray-500">Loading amendment…</span></div>
                )}

                {detail && (
                    <>
                        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-2xl p-6 border-2 border-indigo-200 dark:border-indigo-700">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">{detail.IndentNo}</h2>
                            <p className="text-indigo-600 dark:text-indigo-400 font-semibold mb-3">{detail.CCName}</p>
                            <div className="flex flex-wrap gap-2">
                                <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium">Amend Date: {detail.AmendDate}</span>
                                {detail.CloseStatus === 'Yes' && <span className="px-3 py-1 bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 rounded-full text-xs font-medium">Closes Indent on Approve</span>}
                            </div>
                            <div className="grid grid-cols-3 gap-4 mt-4">
                                <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                    <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase mb-1">Amend Plus Value</p>
                                    <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">₹{money(detail.AmendPlusValue)}</p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                    <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase mb-1">Amend Minus Value</p>
                                    <p className="text-sm font-bold text-rose-600 dark:text-rose-400">₹{money(detail.AmendMinusValue)}</p>
                                </div>
                                <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                    <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase mb-1">Difference Value</p>
                                    <p className="text-sm font-bold text-indigo-600 dark:text-indigo-400">₹{money(detail.DifferenceValue)}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border-2 border-gray-200 dark:border-gray-700">
                            <div className="flex items-center space-x-2 mb-3">
                                <div className="p-1.5 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-lg"><FileText className="w-4 h-4 text-white" /></div>
                                <h3 className="text-sm font-bold text-gray-900 dark:text-white">Indent Items</h3>
                            </div>

                            {items.length === 0 ? (
                                <p className="text-center text-sm text-gray-400 py-8">No amended items found.</p>
                            ) : (
                                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                                        <thead className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20">
                                            <tr>
                                                <th className="px-2 py-1.5 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase">Item Code</th>
                                                <th className="px-2 py-1.5 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase">Item Name</th>
                                                <th className="px-2 py-1.5 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase">Specification</th>
                                                <th className="px-2 py-1.5 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase">DCA / Sub DCA</th>
                                                <th className="px-2 py-1.5 text-right font-semibold text-gray-700 dark:text-gray-300 uppercase">Basic Price</th>
                                                <th className="px-2 py-1.5 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase">Units</th>
                                                <th className="px-2 py-1.5 text-right font-semibold text-gray-700 dark:text-gray-300 uppercase">Current Qty</th>
                                                <th className="px-2 py-1.5 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase">Amend Type</th>
                                                <th className="px-2 py-1.5 text-right font-semibold text-gray-700 dark:text-gray-300 uppercase">Amend Qty</th>
                                                <th className="px-2 py-1.5 text-right font-semibold text-gray-700 dark:text-gray-300 uppercase">Amount</th>
                                                <th className="px-2 py-1.5 text-right font-semibold text-gray-700 dark:text-gray-300 uppercase">New Qty</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                            {items.map((row, idx) => (
                                                <tr key={row.Amendindentid || idx} className="hover:bg-indigo-50 dark:hover:bg-indigo-900/10">
                                                    <td className="px-2 py-1 whitespace-nowrap font-medium">{row.ItemCode}</td>
                                                    <td className="px-2 py-1">{row.ItemName}</td>
                                                    <td className="px-2 py-1">{row.Specification}</td>
                                                    <td className="px-2 py-1 whitespace-nowrap">{row.DcaCode}{row.SubDcaCode ? ` / ${row.SubDcaCode}` : ''}</td>
                                                    <td className="px-2 py-1 text-right">{money(row.BasicPrice)}</td>
                                                    <td className="px-2 py-1 whitespace-nowrap">{row.Units}</td>
                                                    <td className="px-2 py-1 text-right">{num(row.OldQty).toFixed(2)}</td>
                                                    <td className="px-2 py-1 whitespace-nowrap">
                                                        <span className={row.AmendType === 'Add' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}>{row.AmendType}</span>
                                                    </td>
                                                    <td className="px-2 py-1 text-right">{num(row.AmendQty).toFixed(2)}</td>
                                                    <td className="px-2 py-1 text-right">{money(row.Amount)}</td>
                                                    <td className="px-2 py-1 text-right font-semibold">{num(row.NewQuantity).toFixed(2)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        <RemarksHistory
                            isOpen={showRemarksHistory}
                            onToggle={() => setShowRemarksHistory(!showRemarksHistory)}
                            remarks={remarks}
                            loading={remarksLoading}
                            title="Approval History"
                        />

                        <SimpleCommentInput
                            comment={comment}
                            onCommentChange={(e) => setComment(e.target.value)}
                            label="Notes"
                            placeholder="Enter verification notes…"
                            required
                            rows={3}
                            showCharCount
                        />

                        {statusLoading ? (
                            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                                <div className="flex items-center justify-center space-x-3">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                                    <span className="text-gray-600 dark:text-gray-400">Loading actions…</span>
                                </div>
                            </div>
                        ) : statusError ? (
                            <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                                <p className="text-red-600 dark:text-red-400 text-center">⚠️ Error loading actions: {statusError}</p>
                            </div>
                        ) : !hasActions || !enabledActions || enabledActions.length === 0 ? (
                            <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-6 border border-yellow-200 dark:border-yellow-700">
                                <p className="text-yellow-700 dark:text-yellow-400 text-center">ℹ️ No actions available for this amendment</p>
                            </div>
                        ) : (
                            <ActionButtons
                                actions={enabledActions}
                                onActionClick={handleActionClick}
                                loading={verifyLoading}
                                isVerified={true}
                                comment={comment}
                                showValidation={true}
                                excludeActions={EXCLUDED_ACTIONS}
                            />
                        )}
                    </>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <InboxHeader
                title={`${InboxTitle || 'Indent Amend Verification'} (${queue.length})`}
                subtitle={ModuleDisplayName}
                itemCount={queue.length}
                onBackClick={handleBackToInbox}
                HeaderIcon={FileEdit}
                badgeText="Indent Amend Verification"
                badgeCount={queue.length}
                searchConfig={{
                    enabled: true,
                    placeholder: 'Search by indent no, CC…',
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value),
                }}
                enableViewToggle
            />

            <InboxSplitLayout
                isLeftPanelCollapsed={isLeftPanelCollapsed}
                onLeftPanelCollapseToggle={setIsLeftPanelCollapsed}
                isLeftPanelHovered={isLeftPanelHovered}
                onLeftPanelHoverChange={setIsLeftPanelHovered}
                left={{
                    items: filteredQueue,
                    selectedItem,
                    onItemSelect: handleItemSelect,
                    renderItem: renderItemCard,
                    renderListItem,
                    renderCollapsedItem,
                    loading: queueLoading,
                    error: queueError,
                    onRefresh: handleRefresh,
                    config: {
                        title: 'Pending',
                        icon: Clock,
                        emptyMessage: 'No indent amendments pending verification!',
                        itemKey: 'AmendId',
                        enableCollapse: true,
                        enableRefresh: true,
                        enableHover: true,
                        maxHeight: '100%',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                    },
                    renderPopupContent: () => renderDetailContent(),
                    popupConfig: {
                        title: 'Indent Amend Verification',
                        icon: FileEdit,
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxWidth: 'max-w-[80vw]',
                    },
                }}
                right={{
                    selectedItem,
                    loading: false,
                    renderContent: renderDetailContent,
                    config: {
                        title: 'Amendment Details',
                        icon: FileEdit,
                        selectedTitle: 'Indent Amend Verification',
                        emptyTitle: 'No Amendment Selected',
                        emptyMessage: 'Select an indent amendment from the list to view details and take action.',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxHeight: 'calc(100vh-200px)',
                        sticky: true,
                        stickyTop: '1.5rem',
                    },
                }}
            />

            {pendingAction && (
                <ConfirmModal
                    title="Do You Want To Close the Indent"
                    message={`This will close Indent No: ${selectedItem?.IndentNo}. Continue?`}
                    confirmLabel="OK"
                    cancelLabel="Cancel"
                    onCancel={() => { setPendingAction(null); toast.info('Please Reject the Indent Amend'); }}
                    onConfirm={() => { const a = pendingAction; setPendingAction(null); submitAction(a); }}
                />
            )}
        </div>
    );
};

export default VerifyIndentAmend;
